import { type VariantProps } from 'class-variance-authority';
import Link from 'next/link';
import * as React from 'react';

import { Button, buttonVariants } from '@ui/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@ui/components/ui/tooltip';
import { cn } from '@ui/lib/utils';

interface NavButtonProps extends React.ComponentProps<typeof Button>, VariantProps<typeof buttonVariants> {
  href: string;
  currentPathname: string;
  children: React.ReactNode;
  tooltip?: string;
  /**
   * Optional numeric badge shown as a small neutral pill overlay on the
   * top-right of the icon — e.g. count of upcoming bookings on the bookings
   * nav item. Geometry intentionally matches the notification bell badge
   * (`h-5 min-w-5`, `top-0 right-0`) for visual consistency; color is
   * neutral-dark (not red) so it reads as ambient count information rather
   * than an alert, keeping the red destructive color reserved for the
   * notification bell where it signals actionable events.
   *
   * Hidden when falsy or <= 0. Values > 99 are displayed as "99+" to bound
   * width.
   */
  badge?: number;
  /**
   * Accessible label for the badge (read by screen readers). Should describe
   * what the number means, e.g. "3 upcoming bookings". When omitted, the
   * badge is exposed only as a number, which is ambiguous out of context.
   */
  badgeAriaLabel?: string;
  /**
   * Explicit active-state override. When provided, bypasses the automatic
   * path-based detection so callers can highlight the button for routes that
   * don't share a common prefix with `href` (e.g. the settings gear covering
   * public pages like /faq and /privacy-policy that live in the settings sidebar).
   */
  isActive?: boolean;
  /**
   * Forwarded to next/link's `prefetch` prop. Default `null` ("auto" — Next
   * prefetches when the link enters the viewport, but only the static portion
   * for App Router routes). Pass `false` to disable prefetch entirely for
   * cases where the target is unlikely to be navigated to early (and would
   * otherwise pull heavy chunks like Luxon / Zod onto pages that don't need
   * them, as the Lighthouse "unused-javascript" audit kept flagging).
   */
  prefetch?: boolean | null;
}

/**
 * Strip a leading locale segment (e.g. `/en`, `/fr`) so active-state
 * comparison works regardless of whether callers pass locale-prefixed
 * hrefs or not.
 */
const stripLocale = (path: string) => path.replace(/^\/(en|fr)(?=\/|$)/, '') || '/';

function NavButton({
  href,
  currentPathname,
  children,
  className,
  variant = 'ghost',
  size = 'icon',
  tooltip,
  badge,
  badgeAriaLabel,
  isActive: isActiveProp,
  prefetch = null,
  ...props
}: NavButtonProps) {
  // Normalize both sides: strip locale prefix so `/en` vs `/en/foo` comparisons
  // are stable, and default empty paths to "/" so the root case is well-defined.
  const normalizedHref = stripLocale(href);
  const normalizedPath = stripLocale(currentPathname);

  // The root route ("/") must use exact match — otherwise `includes` is
  // trivially true for every path and the Home button would light up on
  // every page. Nested routes keep the existing "prefix / includes" behavior
  // so e.g. `/activity` highlights for `/activity/123/edit`.
  const computedActive = normalizedHref === '/' ? normalizedPath === '/' : normalizedPath.includes(normalizedHref);
  const isActive = isActiveProp !== undefined ? isActiveProp : computedActive;

  // Only render the badge when there's a non-zero count to show — an empty
  // badge is visual noise. Values over 99 are capped to "99+" (same cap as
  // the mobile bottom nav for consistency).
  const showBadge = badge != null && badge > 0;
  const badgeLabel = showBadge ? (badge > 99 ? '99+' : String(badge)) : null;

  const buttonInner = (
    <Button
      variant={variant}
      size={size}
      // Pass aria-label down to the underlying <button> as well as the outer
      // <Link>. The button-name audit looks at each <button> element
      // individually; without this, an icon-only NavButton has an accessible
      // Link but the nested <button> still has no accessible name and
      // Lighthouse flags it.
      aria-label={tooltip}
      className={cn(
        className,
        'cursor-pointer',
        // Keep the badge positioned against the Button box, not the Link, so
        // the overlay clings to the visible chrome regardless of link padding.
        showBadge && 'relative',
        isActive && 'border-b-2 border-primary rounded-t-lg rounded-b-none'
      )}
      {...props}
    >
      {children}
      {showBadge && (
        <span
          aria-label={badgeAriaLabel}
          className={cn(
            // Geometry mirrors NotificationBadge exactly — h-5 min-w-5, rounded
            // pill, px-1, text-xs, pinned to the top-right corner (top-0
            // right-0). Keeping the two badges visually identical in size and
            // position means the header reads as a consistent row of "chip +
            // icon" glyphs instead of a mismatched set of indicators.
            'absolute top-0 right-0 h-5 min-w-5 rounded-full px-1',
            // Color is intentionally NOT destructive. Red is reserved for the
            // notification bell, which signals actionable events; upcoming
            // bookings are ambient count information, so a neutral-dark pill
            // reads as "here's a number" rather than "act now".
            'bg-neutral-800 text-white',
            'text-xs font-semibold leading-none',
            'flex items-center justify-center'
          )}
        >
          {badgeLabel}
        </span>
      )}
    </Button>
  );

  // The Link is the focusable accessible target — Lighthouse's `link-name`
  // and `button-name` audits both look at the rendered element here. Use the
  // tooltip text as the accessible name when the button is icon-only (no
  // visible text children), otherwise let the inner Button's text speak for
  // itself.
  const button = (
    <Link href={href} prefetch={prefetch} aria-label={tooltip}>
      {buttonInner}
    </Link>
  );

  if (tooltip) {
    return (
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>{button}</TooltipTrigger>
          <TooltipContent>{tooltip}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return button;
}

export { NavButton };
